/**
 * How the shuffle works, and a tool that checks it.
 *
 * The page exists because "the deck is fair" is a claim, and a claim a player cannot
 * check is worth nothing. Everything below is either a statement about how the code
 * works — which the verifier lets you confirm for yourself, on a hand you actually
 * played — or an explicit statement of what we are NOT claiming. The second half is
 * the part most sites leave out.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { sha256, toHex, fromHex, deriveSeed } from "@engine/rng";
import { shuffledDeck, isLegalDeck } from "@engine/deck";
import { blackjackShoe, isLegalShoe, shoeSeed, shoeLabel } from "@engine/blackjack/shoe";
import { DOUBLE_DECK_S17 } from "@engine/blackjack/rules";
import { cardName, suitChar, suitIsRed } from "../lib/table-game";

type Mode = "hand" | "session" | "blackjack";

type Check =
  | { state: "idle" }
  | { state: "bad"; why: string }
  | { state: "ok"; commitment: string; matches: boolean | null; deck: number[]; handSeed: string };

/** What the seed box is asking for, per mode. Kept in one place so the copy matches. */
const SEED_LABEL: Record<Mode, string> = {
  hand: "Hand seed",
  session: "Session seed",
  blackjack: "Blackjack session seed",
};

export function Fairness() {
  /**
   * Two modes over one piece of arithmetic.
   *
   * A hand seed goes straight to a deck. A session seed goes through one more step —
   * SHA-256 of the seed and the hand's label — and then to a deck, which is exactly
   * what the engine does, so checking a session here is checking the same code path a
   * player was dealt from.
   */
  const [mode, setMode] = useState<Mode>("hand");
  const [seed, setSeed] = useState("");
  const [claimed, setClaimed] = useState("");
  const [handNo, setHandNo] = useState(1);

  /**
   * Arrive pre-filled from a table.
   *
   * The blackjack room links here with its seed and hand number, because "copy this
   * 64-character string into a box on another page" is the kind of instruction that
   * makes a verification tool decorative. A seed is a public value by construction —
   * it is published precisely so it can be checked — so it is safe in a link in a way
   * that almost nothing else on this site would be.
   *
   * Validated rather than trusted: anything malformed is ignored and the page opens
   * empty, which is what it would have done anyway.
   */
  const [params] = useSearchParams();
  useEffect(() => {
    const m = params.get("mode");
    if (m === "hand" || m === "session" || m === "blackjack") setMode(m);
    const s = params.get("seed");
    if (s && /^[0-9a-fA-F]{64}$/.test(s)) setSeed(s.toLowerCase());
    const c = params.get("commitment");
    if (c && /^[0-9a-fA-F]{64}$/.test(c)) setClaimed(c.toLowerCase());
    const h = Number(params.get("hand"));
    if (Number.isInteger(h) && h >= 1 && h <= 100000) setHandNo(h);
  }, [params]);

  const check: Check = useMemo(() => {
    const s = seed.trim().toLowerCase();
    if (!s) return { state: "idle" };
    if (!/^[0-9a-f]{64}$/.test(s)) {
      return {
        state: "bad",
        why: `A ${mode === "blackjack" ? "blackjack session" : mode} seed is 64 hexadecimal characters — 32 bytes.`,
      };
    }
    const root = fromHex(s);
    // What the seed you pasted is a commitment TO — for a session, the seed itself;
    // for a hand, the same. The derivation to a hand seed happens after.
    const commitment = toHex(sha256(root));
    const want = claimed.trim().toLowerCase();
    const matches = want === "" ? null : want === commitment;

    /**
     * Blackjack goes through `shoe.ts` rather than through anything written here.
     * That is the whole point of the file existing: this page replays the dealer's
     * own function, so "the verifier agrees with the table" is not a property that
     * two implementations have to keep having.
     */
    if (mode === "blackjack") {
      const shoe = blackjackShoe(root, handNo - 1, DOUBLE_DECK_S17);
      if (!isLegalShoe(shoe, DOUBLE_DECK_S17)) {
        return { state: "bad", why: "That seed did not produce a legal shoe." };
      }
      return {
        state: "ok", commitment, matches, deck: shoe,
        handSeed: toHex(shoeSeed(root, handNo - 1)),
      };
    }

    const bytes = mode === "session" ? deriveSeed(root, `hand:${handNo - 1}`) : root;
    const deck = shuffledDeck(bytes);
    if (!isLegalDeck(deck)) return { state: "bad", why: "That seed did not produce a legal deck." };
    return { state: "ok", commitment, matches, deck, handSeed: toHex(bytes) };
  }, [seed, claimed, mode, handNo]);

  return (
    <div className="page prose">
      <h1>The shuffle</h1>
      <p className="lede">
        Every hand you are dealt comes from a 256-bit seed and a stream cipher, and every
        hand you play records the seed it came from. You can paste that seed in below and
        deal the hand again yourself, in your own browser, and see that the cards were
        decided before you acted.
      </p>

      {/* ------------------------------------------------------------- verify --- */}
      <section className="panel">
        <h2>Check a hand</h2>
        <p className="muted small">
          Open any hand in your hand history and copy its <strong>seed</strong> and its{" "}
          <strong>commitment</strong>. Paste them here. The commitment is published when
          the hand starts, before a card is dealt; the seed is revealed when it ends.
          If the seed hashes to the commitment, the deck could not have been changed
          part-way through the hand.
        </p>

        <div className="row" role="group" aria-label="What to check">
          <button
            type="button"
            className={mode === "hand" ? "btn-small" : "btn-quiet"}
            aria-pressed={mode === "hand"}
            onClick={() => setMode("hand")}
          >One hand</button>
          <button
            type="button"
            className={mode === "session" ? "btn-small" : "btn-quiet"}
            aria-pressed={mode === "session"}
            onClick={() => setMode("session")}
          >A whole session</button>
          <button
            type="button"
            className={mode === "blackjack" ? "btn-small" : "btn-quiet"}
            aria-pressed={mode === "blackjack"}
            onClick={() => setMode("blackjack")}
          >A blackjack shoe</button>
        </div>

        {mode === "session" && (
          <p className="muted small">
            A session seed reproduces every hand of a table at once. Hand <em>n</em> is
            dealt from SHA-256 of the session seed followed by the text{" "}
            <code>hand:</code><em>n−1</em> — so one recorded value replays the lot, and a
            session whose seed matches the commitment published at sit-down is a session
            that was never re-seeded part-way through.
          </p>
        )}

        {mode === "blackjack" && (
          <p className="muted small">
            The blackjack room deals a fresh two-deck shoe for every hand, so each hand
            has its own shuffle. Hand <em>n</em> comes from SHA-256 of the session seed
            followed by the text <code>{shoeLabel(0).replace(/0$/, "")}</code><em>n−1</em>{" "}
            — the same derivation the poker table uses, under a different label so the
            two games can never draw the same cards from the same seed. The room shows
            you its seed at the bottom of the table.
          </p>
        )}

        <label className="field">
          <span>{SEED_LABEL[mode]}</span>
          <input
            className="seedcode" type="text" spellCheck={false} autoComplete="off"
            placeholder="64 hex characters"
            value={seed} onChange={(e) => setSeed(e.target.value)}
          />
        </label>

        <label className="field">
          <span>
            Commitment published before the {mode === "hand" ? "hand" : "session"}{" "}
            <em>(optional)</em>
          </span>
          <input
            className="seedcode" type="text" spellCheck={false} autoComplete="off"
            placeholder="64 hex characters"
            value={claimed} onChange={(e) => setClaimed(e.target.value)}
          />
        </label>

        {mode !== "hand" && (
          <label className="field field-narrow">
            <span>Hand number</span>
            <input
              type="number" min={1} step={1} value={handNo}
              onChange={(e) => setHandNo(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
            />
          </label>
        )}

        {check.state === "bad" && <p className="verify-bad">{check.why}</p>}

        {check.state === "ok" && (
          <div className="verify">
            <p className="small">
              <span className="muted">SHA-256 of that seed is</span>{" "}
              <code className="seedcode">{check.commitment}</code>
            </p>
            {mode !== "hand" && (
              <p className="small">
                <span className="muted">Hand {handNo} is dealt from</span>{" "}
                <code className="seedcode">{check.handSeed}</code>
              </p>
            )}
            {check.matches === true && (
              <p className="verify-ok">
                Match. This is the {mode === "hand" ? "deck" : "session"} that was
                committed to before a card was dealt.
              </p>
            )}
            {check.matches === false && (
              <p className="verify-bad">
                No match. Either the seed or the commitment was mistyped — or that seed is
                not the one that hand was dealt from. Check both, and tell us if it
                persists.
              </p>
            )}
            <h3>
              {mode === "blackjack" ? "The shoe" : "The deck"}{" "}
              {mode === "hand" ? "this seed produces" : `hand ${handNo} is dealt from`}
            </h3>
            <p className="muted small">
              {mode === "blackjack" ? (
                <>
                  In order, top first — 104 cards, two decks, so every card appears
                  twice. Dealt one at a time clockwise starting at the seat on the
                  dealer&rsquo;s left, two rounds, the dealer taking the last card of
                  each round; the dealer&rsquo;s second card is the one held face down.
                </>
              ) : (
                <>
                  In order, top first. Hole cards go out one at a time clockwise from the
                  small blind, two rounds; then a burn and three for the flop, a burn and
                  one for the turn, a burn and one for the river — exactly as a dealer
                  would.
                </>
              )}
            </p>
            <ol className="deckstrip">
              {check.deck.map((c, i) => {
                const { rank, suit } = cardName(c);
                return (
                  <li key={i} className={suitIsRed(suit) ? "mc mc-red" : "mc"}>
                    {rank}{suitChar(suit)}
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </section>

      {/* --------------------------------------------------------- how it works --- */}
      <h2>How the deck is made</h2>

      <h3>256 bits, because 52 cards need 226</h3>
      <p>
        A deck has 52! orderings — 8.07 × 10<sup>67</sup>, or about 2<sup>225.6</sup>. A
        shuffle seeded with fewer than 226 bits cannot reach most of them: they are not
        rare, they are impossible. Worse, the ones that remain can be listed. In 1999 a
        poker site shuffled from a 32-bit clock-derived seed; four billion decks is few
        enough to enumerate, and researchers were able to name every player's hole cards
        after seeing the flop. Our seed is 256 bits, drawn from the operating system's
        cryptographic random source.
      </p>

      <h3>ChaCha20, not <code>Math.random</code></h3>
      <p>
        The seed drives a ChaCha20 keystream (RFC 8439). It is a cipher rather than a
        general-purpose generator for one reason: seeing output must not let you compute
        more output. <code>Math.random</code> has no such property — its internal state
        can be recovered from a short run of its results — and neither do the linear
        generators most shuffles use. Our implementation is checked against the test
        vectors published in the RFC on every build, alongside SHA-256 against the FIPS
        180-4 vectors.
      </p>

      <h3>Fisher–Yates, without the bias</h3>
      <p>
        Cards are placed by Fisher–Yates: walk the deck from the back, swap each card
        with one drawn from the part not yet placed. The usual way of drawing that index
        is <code>random() % n</code>, and it is quietly wrong — 2<sup>32</sup> is not
        divisible by 52, so the low indices come up slightly more often and certain cards
        drift toward certain positions. We reject the biased tail of the range instead
        and draw again, so every index is exactly equally likely.
      </p>

      <h3>Committed before the hand, revealed after</h3>
      <p>
        Each hand's seed is fixed before a single card is dealt, and the SHA-256 hash of
        it — the commitment — is recorded at that moment. The seed itself is published
        when the hand ends. Because you cannot find a second seed with the same hash, a
        deck that matches its commitment is a deck that was decided in advance. That is
        what the tool at the top of this page checks.
      </p>

      <h3>What is tested, and how often</h3>
      <p>
        Every build runs a battery against the shuffle before it can ship. It counts all
        120 orderings of a five-card deck and all 5,040 of a seven-card deck and requires
        them to be equally frequent; it checks every card against every position over
        half a million deals; it looks for any relationship between one position and
        another, and between one hand and the next; and it checks the deal a player
        actually sees against the arithmetic — a pocket pair once in 17 hands, aces once
        in 221, a suited hand 23.5% of the time, a monotone board 0.198% of the time. The
        battery also runs a deliberately broken shuffle through the same tests and
        requires it to <em>fail</em>, so a test that has stopped measuring anything cannot
        pass unnoticed.
      </p>

      {/* ------------------------------------------------------------ the limits --- */}
      <h2>What we are not claiming</h2>
      <p>
        These matter as much as the section above, and we would rather say them plainly
        than have you discover them.
      </p>
      <ul>
        <li>
          <strong>This is a training simulator, not a gambling product.</strong> There is
          no money in play, no house, no rake and nothing to win. The fairness question
          here is "are the cards random and is the record honest", not "is the operator
          taking your money fairly".
        </li>
        <li>
          <strong>Buster Bucks are play money, and a balance is not a wager.</strong>{" "}
          Every seat in every room is bought with them, so a number really does go down
          when you sit and up when you stand. It is still not wagering: Bucks cannot be
          bought, sold, transferred, redeemed or exchanged for anything, there is no
          purchase path and no plan that adds one, and whenever your balance falls below
          500 the Barkeep stakes you back to 1,000 — free, with no daily limit and no
          cooldown. A thing you can have back by asking is not a thing you can lose. Any
          tournament with a real prize is free to enter and takes no Bucks at all.
        </li>
        <li>
          <strong>We are not licensed or certified by any gaming authority.</strong> No
          testing house has audited this shuffle. What we have is the design above, the
          test battery, and code you can read. We have built to the standards those
          bodies publish (GLI-11 and GLI-19 on random number generators) because they are
          the right standards; that is not the same as having passed them, and we will not
          imply otherwise.
        </li>
        <li>
          <strong>The game runs in your browser.</strong> The shuffle happens on your own
          machine, from entropy your own machine supplies. That is stronger than trusting
          a server for randomness — but it also means a player determined to cheat can
          read their own memory and see the deck. There is nothing to win by doing so, and
          nothing we could do to stop it without moving the game to a server and asking
          you to trust us instead.
        </li>
        <li>
          <strong>The bots are not a solver.</strong> They play from a heuristic model
          with a deliberately varied skill ladder. Where a decision is priced for you in
          the hand history, that price comes from the same heuristic, not from a solved
          equilibrium — the solved ranges in the trainer are a separate thing and are
          labelled as such.
        </li>
        <li>
          <strong>Random is not the same as fair-feeling.</strong> A correct shuffle
          produces set-over-set, four-flush boards and long dry spells at exactly their
          real frequency, which is more often than most players expect. The rates above
          are the ones to check us against; a run of bad beats is not evidence of
          anything, in either direction.
        </li>
      </ul>

      <p className="muted small">
        Found something wrong with any of this? That is a real bug and we want it —{" "}
        <a href="mailto:support@bluffbusters.net">support@bluffbusters.net</a>. Back to{" "}
        <Link to="/play">the table</Link>.
      </p>
    </div>
  );
}
